import {EntityListColumn, EntityPage} from 'components';
import {
	renderArrayField,
	renderVexClusters,
	renderVexesPersona,
	renderVkosPersona,
} from 'components/EntityList/ColumnRenderers';
import {LoadWrapper} from 'components/LoadWrapper';
import {ViewAuthorizer} from 'components/ViewAuthorizer';
import {KeywordsTooltipTranslationProvider} from 'features/localizedTooltips';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {Keyword, User, UserRole, VexCluster} from 'types';
import {
	GetAllKeywordsQuery,
	useGetAllKeywordsQuery,
} from '../AdminSection/hooks/keywords.generated';
import {
	GetAllVexClustersQuery,
	useGetAllVexClustersQuery,
} from '../AdminSection/hooks/vexClusters.generated';
import {MessageBar} from '@fluentui/react';
import {HeaderForPagesWithEntityPage} from 'components/HeaderForPagesWithEntityPage';

import _ from 'lodash';
import {ARRAY_TYPE} from 'components/EntityList/EntityUtils';
import {useGetAllVkosQuery} from 'features/AdminSection/hooks/vko.generated';
import {alphabeticalSort, numericalSort} from 'helpers/sortHelpers';
import {getUserDepartment} from 'components/Personas';
import {URLProzess, URLVKO, URLVEX} from './vkovexConstants';

const AdminKeywordsPage: React.FC = () => {
	const {t: tKeywords} = useTranslation('features/adminsection', {
		keyPrefix: 'AdminKeywordsPage',
	});
	const {t: tClusters} = useTranslation('features/adminsection', {
		keyPrefix: 'AdminVexClustersPage',
	});

	const {loading: keywordsLoading, data: keywordData} =
		useGetAllKeywordsQuery();
	const {loading: vexClutersLoading, data: vexClustersData} =
		useGetAllVexClustersQuery();

	const {loading: loadingVkos, data: dataVkos} = useGetAllVkosQuery();

	const keywords: Keyword[] = React.useMemo(
		() =>
			vexClutersLoading || keywordsLoading
				? []
				: getKeywordsWVexes(keywordData, vexClustersData),
		[keywordsLoading, vexClutersLoading, keywordData, vexClustersData],
	);

	const columns: EntityListColumn[] = React.useMemo(
		() => [
			{
				key: 'name',
				name: tKeywords('Keywords'),
				fieldName: 'name',
				minWidth: 150,
				maxWidth: 200,
				isResizable: true,
				filterable: true,
				sortable: true,
				filterOnFilter: true,
			},
			{
				key: 'regulationClusters',
				name: tKeywords('RegulationClusters'),
				fieldName: 'regulationClusters',
				minWidth: 150,
				maxWidth: 200,
				isResizable: true,
				filterable: true,
				sortable: true,
				dataType: ARRAY_TYPE,
				onRender: renderArrayField(),
			},
			{
				key: 'vkos',
				name: tKeywords('Vkos'),
				fieldName: 'vkos',
				minWidth: 200,
				maxWidth: 200,
				filterable: true,
				sortable: true,
				dataType: ARRAY_TYPE,
				onRender: renderVkosPersona(keywords, dataVkos),
				filterOnFilter: true,
			},
			{
				key: 'vexClusters',
				name: tKeywords('VexClusters'),
				fieldName: 'vexClusters',
				minWidth: 150,
				maxWidth: 200,
				isResizable: true,
				filterable: true,
				sortable: true,
				dataType: ARRAY_TYPE,
				onRender: renderVexClusters(),
				filterOnFilter: true,
			},
			{
				key: 'vexes',
				name: tClusters('Vexes'),
				fieldName: 'vexes',
				minWidth: 200,
				maxWidth: 200,
				filterable: true,
				sortable: true,
				dataType: ARRAY_TYPE,
				onRender: renderVexesPersona(keywords),
				filterOnFilter: true,
			},
		],
		[keywords, dataVkos, vexClustersData],
	);

	const strMessageHTML = tKeywords('VkoVexMessage', {
		URLProzess,
		URLVKO,
		URLVEX,
	});
	return (
		<ViewAuthorizer
			roles={[
				UserRole.SystemAdministrator,
				UserRole.Vko,
				UserRole.Vex,
				UserRole.VexSupport,
				UserRole.Readers,
				UserRole.Avk,
			]}
		>
			<HeaderForPagesWithEntityPage>
				<h3>{tClusters('VkoVexList')}</h3>
			</HeaderForPagesWithEntityPage>
			<MessageBar isMultiline={true}>
				<div dangerouslySetInnerHTML={{__html: strMessageHTML}} />
			</MessageBar>
			<LoadWrapper loading={keywordsLoading || vexClutersLoading}>
				<KeywordsTooltipTranslationProvider>
					<EntityPage
						items={keywords as any}
						entityDisplayName={tKeywords('Keywords')}
						createEntity={() => null}
						updateEntity={() => null}
						deleteEntity={() => null}
						withNameColumn={false}
						hideCreate={true}
						hideEdit={true}
						hideDelete={true}
						additionalColumns={columns}
						shouldWrapListWithScrollablePane={false}
					/>
				</KeywordsTooltipTranslationProvider>
			</LoadWrapper>
		</ViewAuthorizer>
	);
};

export default AdminKeywordsPage;

const getKeywordsWVexes = (
	keywordData: GetAllKeywordsQuery | undefined,
	vexClustersData: GetAllVexClustersQuery | undefined,
) => {
	const keywordsUnsorted = keywordData?.keywords ?? [];
	const keywordsSorted = numericalSort(keywordsUnsorted, 'sortPriority');

	const mapVexCluster = new Map<string, number>();
	const vexClusters = vexClustersData?.vexClusters ?? [];
	vexClusters.forEach((x, index) => mapVexCluster.set(x.id, index));

	return keywordsSorted.map((keyword: Keyword) => {
		const keywordVCs: VexCluster[] = [];
		const vexesAll: User[] = [];

		keyword.vexClusters.forEach((vc: VexCluster) => {
			const currentClusterId = mapVexCluster.get(vc.id);
			if (currentClusterId !== undefined) {
				const currentCluster = vexClusters.at(currentClusterId);
				keywordVCs.push(currentCluster as VexCluster);
				currentCluster?.users?.forEach(user => {
					vexesAll.push(user);
				});
			}
		});

		const vexes = _.uniqBy(vexesAll, 'id').sort((a, b) => {
			const departA = getUserDepartment(a as User) ?? 'z';
			const departB = getUserDepartment(b as User) ?? 'z';

			return departA === departB
				? a.name.localeCompare(b.name)
				: departA.localeCompare(departB);
		});

		return {
			...keyword,
			vexClusters: alphabeticalSort(keywordVCs, 'name'),
			vexes,
		};
	});
};
